// @ts-check

import "../jsdoc-types"
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
// @ts-expect-error I don't know how to fix this with JSDoc
import Url from '@core/scripts/helper/url';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Accordion, Button, Collapsible, Link, List, Typography } from '@bonprix/design-system';
import classNames from 'classnames';
import slug from 'slug';
import { TableOfContents } from './TableOfContents';
import { RichTextProvider } from './RichTextContext';
import { Container } from './Container';
import { GridContainer } from './GridContainer';
import { PrivacyCenterLink } from "./PrivacyCenterLink";

/**
 * @param {Object} props
 * @param {RichTextEntry} props.cdaEntry
 */
export function RichText({ cdaEntry }) {
    /**
     * @type {import('@contentful/rich-text-react-renderer').Options}
     */
    const options = {
        renderNode: {
            [BLOCKS.HEADING_1]: (node, children) => <div id={ slug(node.content[0].value) }><Typography variant="h1">{ children }</Typography></div>,
            [BLOCKS.HEADING_2]: (node, children) => <div id={ slug(node.content[0].value) }><Typography variant="h2">{ children }</Typography></div>,
            [BLOCKS.HEADING_3]: (node, children) => <div id={ slug(node.content[0].value) }><Typography variant="h3">{ children }</Typography></div>,
            [BLOCKS.HEADING_4]: (node, children) => <div id={ slug(node.content[0].value) }><Typography variant="h4">{ children }</Typography></div>,
            [BLOCKS.HEADING_5]: (node, children) => <div id={ slug(node.content[0].value) }><Typography variant="h5">{ children }</Typography></div>,
            [BLOCKS.HEADING_6]: (node, children) => <div id={ slug(node.content[0].value) }><Typography variant="h6">{ children }</Typography></div>,
            [BLOCKS.PARAGRAPH]: (node, children) => <Typography variant="body1">{ children }</Typography>,
            // @ts-expect-error The type for children from Link is wrong. It should be ReactNode but it is string (https://github.com/bonprix/design-system/blob/main/src/components/Link/Link.tsx#L32)
            [INLINES.HYPERLINK]: (node, children) => <Link href={ node.data.uri }>{ children }</Link>,
            [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
                if (!node.data.target.fields) {
                    console.warn(`[Service Page Content][RichText] Asset doesn't exist (anymore). It probably got deleted but is still embedded in an other entry (entry ID: ${cdaEntry.sys.id})`);
                    return null;
                }

                if (!node.data.target.fields.file) {
                    console.warn(`[Service Page Content][RichText] Embedded asset has no file (entry ID: ${cdaEntry.sys.id}, asset ID: ${node.data.target.sys.id})`);
                    return null;
                }

                if (node.data.target.fields.file.contentType.startsWith("image/")) {
                    // Width of 856 since this is the maximum width our Rich Text component can be on the service pages.
                    // A more optimized value would require some sort of more complex lazy loading behavior.
                    const url = `https:${node.data.target.fields.file.url}?w=856`;

                    return (
                        <img
                            key={ `${cdaEntry.sys.id}_${node.data.target.sys.id}` }
                            src={ url }
                            alt={ node.data.target.fields.description }
                            width={ node.data.target.fields.file.details.image.width }
                            height={ node.data.target.fields.file.details.image.height }
                            loading="lazy"
                        />
                    );
                } else {
                    console.warn(`[Service Page Content][RichText] Unknown embedded asset type (entry ID: ${cdaEntry.sys.id}, asset ID: ${node.data.target.sys.id})`);
                    return null;
                }
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
                if (!node.data.target) {
                    console.warn(`[Service Page Content][RichText] Entry doesn't exist (anymore). It probably got deleted but is still embedded in an other entry (entry ID: ${cdaEntry.sys.id})`);
                    return null;
                }

                // Prevent duplicate keys when same entry is embedded multiple times on a page
                const key = `${cdaEntry.sys.id}_${node.data.target.sys.id}`;

                switch (node.data.target.sys.contentType.sys.id) {
                    case "accordion":
                        return (
                            <div key={ key } className="service-page-rich-text__accordion">
                                <Accordion>
                                    { node.data.target.fields.entries.map((entry) => (
                                        <Accordion.Item key={ entry.sys.id } title={ entry.fields.title }>
                                            { documentToReactComponents(entry.fields.body, options) }
                                        </Accordion.Item>
                                    )) }
                                </Accordion>
                            </div>
                        );
                    case "collapsible":
                        return (
                            <div key={ key } className="service-page-rich-text__collapsible">
                                <Collapsible title={ node.data.target.fields.title }>
                                    { documentToReactComponents(node.data.target.fields.body, options) }
                                </Collapsible>
                            </div>
                        );
                    case "appStores":
                        return (
                            <div key={ key } className="service-page-rich-text__app-stores">
                                { node.data.target.fields.entries.map((entry) => (
                                    <a key={ entry.sys.id } href={ entry.fields.url }>
                                        <img src={ entry.fields.image?.fields.file?.url } alt={ entry.fields.image?.fields.description } />
                                    </a>
                                )) }
                            </div>
                        );
                    case "button":
                        return (
                            <a key={ key } href={ node.data.target.fields.href }>
                                <Button variant={ node.data.target.fields.variant } onClick={ () => { } }>
                                    { node.data.target.fields.title }
                                </Button>
                            </a>
                        );
                    case "list":
                        return (
                            <div key={ key } className="service-page-rich-text__list">
                                <List type={ node.data.target.fields.type || undefined } showArrows={ node.data.target.fields.showArrows } className={ `list-cols-${node.data.target.fields.columns || 1}` }>
                                    { node.data.target.fields.entries.map((entry) => (
                                        // @ts-expect-error The type for children from List.Item is wrong. It should be ReactNode but it is string (https://github.com/bonprix/design-system/blob/main/src/components/List/ListItem/ListItem.tsx#L32)
                                        <List.Item key={ entry.sys.id } icon={ entry.fields.icon } href={ entry.fields.link ? toAbsoluteUrl(entry.fields.link) : null } >
                                            { documentToReactComponents(entry.fields.body, options) }
                                        </List.Item>
                                    )) }
                                </List>
                            </div>
                        );
                    case "tableOfContents":
                        return <TableOfContents key={ key } />;
                    case "container":
                        return <Container key={ key } cdaEntry={ node.data.target } />;
                    case "gridContainer":
                        return <GridContainer key={ key } cdaEntry={ node.data.target } />;
                    default:
                        console.warn(`[Service Page Content][RichText] Unknown embedded block entry type (entry ID: ${node.data.target.sys.id})`);
                        return null;
                }
            },
            [INLINES.EMBEDDED_ENTRY]: (node) => {
                if (!node.data.target) {
                    console.warn(`[Service Page Content][RichText] Inline entry doesn't exist (anymore). It probably got deleted but is still embedded in an other entry (entry ID: ${cdaEntry.sys.id})`);
                    return null;
                }

                // Prevent duplicate keys when same entry is embedded multiple times on a page
                const key = `${cdaEntry.sys.id}_${node.data.target.sys.id}`;

                switch (node.data.target.sys.contentType.sys.id) {
                    case "privacyCenterLink":
                        return <PrivacyCenterLink key={ key } cdaEntry={ node.data.target } />;
                    default:
                        console.warn(`[Service Page Content][RichText] Unknown embedded inline entry type (entry ID: ${node.data.target.sys.id})`);
                        return null;
                }
            }
        },
        preserveWhitespace: true,
    }

    return (
        <RichTextProvider cdaEntry={ cdaEntry }>
            <div className={classNames("service-page-rich-text", {
                "text-align-left": !cdaEntry.fields.textAlignment || cdaEntry.fields.textAlignment === "left",
                "text-align-center": cdaEntry.fields.textAlignment === "center",
                "text-align-right": cdaEntry.fields.textAlignment === "right",
            })}>
                { documentToReactComponents(cdaEntry.fields.text, options) }
            </div>
        </RichTextProvider>
    );
}

function toAbsoluteUrl(url) {
    // Don't change already absolute URLs
    if(/((https?:\/\/)|(\/\/))/i.test(url)) {
        return url;
    }
    return Url.toAbsolute(url);
}
